document.addEventListener('DOMContentLoaded', function () {
  const article = document.querySelector('.article-faq-content');

  if (!article) return;

  /* -------------------------------------------------------
     HELPERS
  ------------------------------------------------------- */

  function getText(element) {
    return (element.textContent || '')
      .replace(/\u00a0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function getHeadingLevel(element) {
    const match = element.tagName.match(/^H([1-6])$/);

    return match ? Number(match[1]) : null;
  }

  function isFaqHeading(element) {
    if (!/^H[1-6]$/.test(element.tagName)) {
      return false;
    }

    const text = getText(element)
      .toLowerCase()
      .replace(/[?:\-–—]/g, '')
      .trim();

    return (
      text === 'faq' ||
      text === 'faqs' ||
      text === 'frequently asked questions' ||
      text === 'frequently asked question'
    );
  }

  /*
   * Shopify's rich-text editor / Word / Google Docs can
   * sometimes wrap headings or paragraphs inside DIVs.
   *
   * We unwrap simple wrappers before detecting FAQs.
   */
  function isEditorWrapper(element) {
    if (element.tagName !== 'DIV') return false;

    if (
      element.classList.contains('article-faq-accordion') ||
      element.classList.contains('faq-item')
    ) {
      return false;
    }

    const children = Array.from(element.children);

    if (!children.length) return false;

    /*
     * Common Microsoft Word / editor wrapper.
     */
    if (
      element.classList.contains('OutlineElement') ||
      element.getAttribute('data-mce-style') ||
      element.getAttribute('data-mce-fragment')
    ) {
      return true;
    }

    /*
     * Simple DIV containing one heading or paragraph.
     */
    if (
      children.length === 1 &&
      /^(P|H1|H2|H3|H4|H5|H6)$/.test(children[0].tagName)
    ) {
      return true;
    }

    return false;
  }

  function unwrapElement(element) {
    const parent = element.parentNode;

    while (element.firstChild) {
      parent.insertBefore(element.firstChild, element);
    }

    element.remove();
  }

  /*
   * Clean prefixes such as:
   *
   * Q. What is...
   * Q: What is...
   * Q - What is...
   * Question: What is...
   */
  function cleanQuestionText(text) {
    return text
      .replace(
        /^(?:q(?:uestion)?\s*[\.\:\-\)]\s*)/i,
        ''
      )
      .trim();
  }

  function cleanAnswerElement(element) {
    const clone = element.cloneNode(true);

    /*
     * Remove:
     *
     * A.
     * A:
     * Answer:
     */
    if (/^(P|DIV)$/.test(clone.tagName)) {
      const firstTextNode = Array.from(clone.childNodes).find(
        node =>
          node.nodeType === Node.TEXT_NODE &&
          node.textContent.trim()
      );

      if (firstTextNode) {
        firstTextNode.textContent =
          firstTextNode.textContent.replace(
            /^(?:a(?:nswer)?\s*[\.\:\-\)]\s*)/i,
            ''
          );
      }
    }

    return clone;
  }

  /* -------------------------------------------------------
     QUESTION DETECTION
  ------------------------------------------------------- */

  function isQuestion(element, faqHeadingLevel) {
    const text = getText(element);

    if (!text) return false;

    const headingLevel = getHeadingLevel(element);

    /*
     * CASE 1
     * Heading below the FAQ heading.
     *
     * Example:
     *
     * H2 → FAQs
     * H3 → Question
     *
     * OR
     *
     * H3 → FAQs
     * H4 → Question
     */
    if (
      headingLevel &&
      headingLevel > faqHeadingLevel
    ) {
      return true;
    }

    /*
     * CASE 2
     *
     * Q. What is...
     * Q: What is...
     * Q - What is...
     * Question: What is...
     */
    if (
      /^(?:q(?:uestion)?\s*[\.\:\-\)])/i.test(text)
    ) {
      return true;
    }

    /*
     * CASE 3
     *
     * <p><strong>What is a diamond?</strong></p>
     *
     * or
     *
     * <p><b>What is a diamond?</b></p>
     */
    if (element.tagName === 'P') {
      const children = Array.from(element.children);

      if (
        children.length === 1 &&
        /^(STRONG|B)$/.test(children[0].tagName) &&
        text.endsWith('?')
      ) {
        return true;
      }
    }

    /*
     * CASE 4
     *
     * Normal short paragraph ending with ?
     *
     * This catches Shopify content where the editor
     * hasn't used proper heading tags.
     */
    if (
      element.tagName === 'P' &&
      text.endsWith('?') &&
      text.length <= 250 &&
      text.split(/\s+/).length <= 35
    ) {
      return true;
    }

    /*
     * CASE 5
     *
     * Question directly inside a DIV.
     */
    if (
      element.tagName === 'DIV' &&
      element.children.length === 0 &&
      text.endsWith('?') &&
      text.length <= 250
    ) {
      return true;
    }

    return false;
  }

  /* -------------------------------------------------------
     NORMALIZE EDITOR HTML
  ------------------------------------------------------- */

  Array.from(article.querySelectorAll('div')).forEach(
    function (div) {
      if (isEditorWrapper(div)) {
        unwrapElement(div);
      }
    }
  );

  /* -------------------------------------------------------
     FIND FAQ SECTIONS
  ------------------------------------------------------- */

  const faqHeadings = Array.from(
    article.querySelectorAll('h1,h2,h3,h4,h5,h6')
  ).filter(isFaqHeading);

  if (!faqHeadings.length) return;

  faqHeadings.forEach(function (faqHeading, sectionIndex) {
    /*
     * Don't process the same FAQ section twice.
     */
    if (faqHeading.dataset.faqProcessed === 'true') {
      return;
    }

    faqHeading.dataset.faqProcessed = 'true';

    const faqHeadingLevel =
      getHeadingLevel(faqHeading) || 2;

    const accordion = document.createElement('div');

    accordion.className = 'article-faq-accordion';

    accordion.setAttribute(
      'data-faq-section',
      String(sectionIndex + 1)
    );

    let current = faqHeading.nextElementSibling;

    const faqGroups = [];

    let currentQuestion = null;
    let currentAnswers = [];

    /* -----------------------------------------------------
       READ EVERYTHING AFTER "FAQ"
    ----------------------------------------------------- */

    while (current) {
      const next = current.nextElementSibling;

      const currentHeadingLevel =
        getHeadingLevel(current);

      /*
       * Stop when we reach another section heading
       * at the same or higher level.
       *
       * Example:
       *
       * H2 FAQs
       *   H3 Question
       *   P Answer
       *
       * H2 Conclusion ← STOP
       */
      if (
        currentHeadingLevel &&
        currentHeadingLevel <= faqHeadingLevel
      ) {
        break;
      }

      if (isQuestion(current, faqHeadingLevel)) {
        /*
         * Save previous question.
         */
        if (currentQuestion) {
          faqGroups.push({
            question: currentQuestion,
            answers: currentAnswers
          });
        }

        currentQuestion = current;
        currentAnswers = [];
      } else if (currentQuestion) {
        /*
         * Anything following a detected question becomes
         * part of its answer.
         *
         * This supports:
         *
         * paragraphs
         * lists
         * tables
         * blockquotes
         * images
         * videos
         * links
         * DIVs
         * etc.
         */
        currentAnswers.push(current);
      }

      current = next;
    }

    /*
     * Save final question.
     */
    if (currentQuestion) {
      faqGroups.push({
        question: currentQuestion,
        answers: currentAnswers
      });
    }

    /*
     * If we couldn't detect a valid FAQ pair,
     * leave the original content untouched.
     */
    if (!faqGroups.length) return;

    /* -----------------------------------------------------
       BUILD ACCORDION
    ----------------------------------------------------- */

    faqGroups.forEach(function (group, index) {
      const questionText = cleanQuestionText(
        getText(group.question)
      );

      if (!questionText) return;

      const item = document.createElement('div');

      item.className = 'faq-item';

      const questionButton =
        document.createElement('button');

      questionButton.type = 'button';
      questionButton.className = 'faq-question';

      const answerId =
        `faq-answer-${sectionIndex}-${index}`;

      questionButton.setAttribute(
        'aria-expanded',
        'false'
      );

      questionButton.setAttribute(
        'aria-controls',
        answerId
      );

      const questionTextElement =
        document.createElement('span');

      questionTextElement.className =
        'faq-question-text';

      questionTextElement.textContent =
        questionText;

      const icon = document.createElement('span');

      icon.className = 'faq-icon';
      icon.setAttribute('aria-hidden', 'true');

      questionButton.appendChild(
        questionTextElement
      );

      questionButton.appendChild(icon);

      const answer = document.createElement('div');

      answer.className = 'faq-answer';
      answer.id = answerId;
      answer.hidden = true;

      group.answers.forEach(function (answerElement) {
        answer.appendChild(
          cleanAnswerElement(answerElement)
        );
      });

      item.appendChild(questionButton);
      item.appendChild(answer);

      accordion.appendChild(item);
    });

    /*
     * Insert accordion directly after FAQ heading.
     */
    faqHeading.insertAdjacentElement(
      'afterend',
      accordion
    );

    /*
     * Remove original FAQ questions + answers because
     * they've now been copied into the accordion.
     */
    faqGroups.forEach(function (group) {
      group.question.remove();

      group.answers.forEach(function (answer) {
        answer.remove();
      });
    });

    /* -----------------------------------------------------
       ACCORDION CLICK
    ----------------------------------------------------- */

    accordion
      .querySelectorAll('.faq-question')
      .forEach(function (button) {
        button.addEventListener(
          'click',
          function () {
            const item =
              button.closest('.faq-item');

            const answer =
              item.querySelector('.faq-answer');

            const isOpen =
              button.getAttribute(
                'aria-expanded'
              ) === 'true';

            button.setAttribute(
              'aria-expanded',
              String(!isOpen)
            );

            answer.hidden = isOpen;

            item.classList.toggle(
              'is-open',
              !isOpen
            );
          }
        );
      });
  });
});